import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {PopoverCoordinator} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {isJobRunning} from '../../../../helpers/jobHelpers';
import {AccountModel} from '../../../../models/api/accountModel';
import {AccountDomainAssociationJobModel} from '../../../../models/api/jobModel';
import {ContactOrAccountDetailsPanelId} from '../../../../store/panels/panelStateBase';
import {EmailDomainDropdownModesEnum} from './emailDomainDropdown';
import {EmailDomainDropdownContainer} from './emailDomainDropdownContainer';

/*
 * Props.
 */

interface EmailDomainEditButtonPopoverCoordinatorProps {
  account: AccountModel;
  panelId: ContactOrAccountDetailsPanelId;
  jobs: ReadonlyArray<AccountDomainAssociationJobModel>;
  onCancel?: Handler;
}

/*
 * Styles.
 */

const StyledIconButton = styled(IconButton)<{isDisabled?: boolean}>`
  ${(p) =>
    p.isDisabled &&
    css`
      cursor: not-allowed;
    `}
`;

/*
 * Component.
 */

export const EmailDomainEditButtonPopoverCoordinator: FC<EmailDomainEditButtonPopoverCoordinatorProps> = (
  props,
) => {
  const theme = useUserTheme();
  const {account, panelId, jobs, onCancel} = props;
  const isDisabled = computeIsConflict(jobs, account);

  return (
    <PopoverCoordinator
      renderButton={() => (
        <StyledIconButton
          iconName="editSmall"
          color={theme.greys.shade70}
          label={renderEditLabel(jobs, account)}
          isDisabled={isDisabled} // Applies the style which changes the cursor type, and tells the IconButton to set isDisabled on its Icon
        />
      )}
      renderModal={(popoverProps) => (
        <EmailDomainDropdownContainer
          panelId={panelId}
          account={account}
          mode={EmailDomainDropdownModesEnum.EDIT}
          popoverTransferProps={popoverProps}
          onCancel={onCancel || popoverProps.onRequestClose}
          onSave={popoverProps.onRequestClose}
        />
      )}
      onPopoverToggle={(isModalOpen: boolean) => {
        // The user can also click away to close the modal so we should also hide the controls then
        if (onCancel && !isModalOpen) {
          onCancel();
        }
      }}
      isDisabled={isDisabled}
    />
  );
};

/*
 * Helpers.
 */

function computeIsConflict(
  jobs: ReadonlyArray<AccountDomainAssociationJobModel>,
  account: AccountModel,
): boolean {
  // Filter down to Jobs which are active and on the currently selected Account
  const conflictingJobs = jobs.filter((job) => job.account_id === account.id).filter(isJobRunning);
  return conflictingJobs.length > 0;
}

function renderEditLabel(jobs: ReadonlyArray<AccountDomainAssociationJobModel>, account: AccountModel) {
  if (computeIsConflict(jobs, account)) {
    return <Trans id="account-details-email-domains-busy">Associating contacts, please wait...</Trans>;
  }

  return <Trans id="account-details-email-domains-edit">Edit</Trans>;
}
